import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import WorkbenchShell from "./WorkbenchShell";
import JobsRail, { formatJobDate, formatJobStatus } from "./JobsRail";
import { formatCurrency } from "../../utils/formatters";

/**
 * `/jobs` — the saved-jobs entry rail.
 *
 * Highlighting a row only previews it; loading a job into the estimate
 * workbench takes the explicit "Open in workbench" button, so a mis-click in
 * the rail can never discard unsaved work on the estimate screen.
 */

function SummaryRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="jobs-detail__row">
      <span className="jobs-detail__k">{label}</span>
      <span className="jobs-detail__v">{value}</span>
    </div>
  );
}

function JobsWorkbenchPage({ onJobSelect, currentJobId = null }) {
  const navigate = useNavigate();
  const [jobCount, setJobCount] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isOpening, setIsOpening] = useState(false);

  const handleJobsLoaded = useCallback(
    (jobs) => {
      setJobCount(jobs.length);
      setSelectedJob((current) => {
        if (current) return current;
        if (currentJobId) {
          const active = jobs.find((job) => job.id === currentJobId);
          if (active) return active;
        }
        return jobs[0] || null;
      });
    },
    [currentJobId],
  );

  const handleOpen = async () => {
    if (!selectedJob || isOpening) return;
    setIsOpening(true);
    try {
      if (typeof onJobSelect === "function") {
        // The opener navigates to the estimate screen itself once the load
        // has actually happened. It cannot be done here: with unsaved work on
        // the estimate screen the opener parks the call behind a confirm and
        // resolves at once, so "awaited without throwing" no longer means
        // "opened" — navigating on it would strand a Cancel on the wrong page.
        await onJobSelect(selectedJob);
      }
    } catch (error) {
      // The open failed and the estimate screen still holds whatever was open
      // before. Stay put; the opener has already surfaced the error.
      console.error("Failed to open job:", error);
    } finally {
      setIsOpening(false);
    }
  };

  const hasStoredPrice =
    selectedJob?.final_price !== null &&
    selectedJob?.final_price !== undefined &&
    selectedJob?.final_price !== "";

  const center = selectedJob ? (
    <div className="jobs-detail">
      <p className="wb-eyebrow">Saved estimate</p>
      <h1 className="wb-h1">{selectedJob.job_name || "Untitled job"}</h1>
      <p className="wb-sub">
        {selectedJob.customer_name || "No customer on file"}
      </p>

      <div className="jobs-detail__grid">
        <SummaryRow
          label="Status"
          value={formatJobStatus(selectedJob.status)}
        />
        <SummaryRow
          label="Versions"
          value={
            selectedJob.version_count === null ||
            selectedJob.version_count === undefined
              ? null
              : String(selectedJob.version_count)
          }
        />
        <SummaryRow
          label="Last updated"
          value={formatJobDate(selectedJob.updated_at)}
        />
        <SummaryRow
          label="Created by"
          value={selectedJob.creator?.username || null}
        />
        {hasStoredPrice ? (
          <SummaryRow
            label="Quoted price"
            value={formatCurrency(selectedJob.final_price)}
          />
        ) : null}
      </div>

      <div className="jobs-detail__actions">
        <button
          type="button"
          className="wb-tbtn wb-tbtn--solid"
          onClick={handleOpen}
          disabled={isOpening}
        >
          {isOpening ? "Opening…" : "Open in workbench"}
        </button>
        {/* The quote as a document — a read-only page, so it never has to ask
            about unsaved work the way opening the job does. */}
        <button
          type="button"
          className="wb-tbtn"
          onClick={() => navigate(`/jobs/${selectedJob.id}/quote`)}
        >
          Quote
        </button>
        <button
          type="button"
          className="wb-tbtn"
          title="Sheets and paint to buy for this job's rafter tails"
          onClick={() => navigate(`/jobs/${selectedJob.id}/order-sheet`)}
        >
          Order sheet
        </button>
        {currentJobId === selectedJob.id ? (
          <span className="jobs-detail__hint">
            Currently loaded in the estimate workbench.
          </span>
        ) : (
          <span className="jobs-detail__hint">
            Opening replaces whatever is loaded on the estimate screen.
          </span>
        )}
      </div>
    </div>
  ) : (
    <div className="jobs-detail">
      <p className="wb-eyebrow">Saved estimates</p>
      <h1 className="wb-h1">Pick a job</h1>
      <p className="wb-sub">
        Choose a saved job from the list to see its summary, then open it in the
        estimate workbench.
      </p>
    </div>
  );

  return (
    <WorkbenchShell
      variant="estimate"
      title="Jobs"
      subtitle="Saved estimates"
      rail={
        <JobsRail
          selectedJobId={selectedJob?.id ?? null}
          onSelectJob={setSelectedJob}
          onJobsLoaded={handleJobsLoaded}
        />
      }
      railHeader={{
        label: "Saved jobs",
        count: jobCount === null ? undefined : jobCount,
      }}
      center={center}
      centerHeader={{ label: selectedJob ? "Job summary" : "No job selected" }}
      inspector={null}
      statusBar={null}
    />
  );
}

export default JobsWorkbenchPage;
